import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
} from "@mui/material";

import { Transaction } from "../../../api/types/transactions.types";
import { CurrencyEnum } from "../../../../../enums/currencies.enum";
import { formatLocalizedTimeAMPM } from "../../../../../utils/formatDate";

import { TransactionStatusEnum } from "../../../enums/payment";
import { FailedStatusIcon, SuccessStatusIcon, ViewIconButton } from "../../../assets/icons/plans-icons";

import { formatCardNumber } from "./utils";
import { TransactionModal, useTransactionModal } from "./TransactionModal";

type TransactionsTableProps = {
  data: Transaction[];
};

const amountFormatter = (amount: number, currency: CurrencyEnum, locale: string = "en") => {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
};

const StatusIcon = ({ status }: { status: TransactionStatusEnum }) => {
  return status === TransactionStatusEnum.Success ? <SuccessStatusIcon /> : <FailedStatusIcon />;
};

const TransactionsTable = ({ data }: TransactionsTableProps) => {
  const { t, i18n } = useTranslation("plans", { keyPrefix: "page.plansOverviewPage" });
  const { t: tEnum } = useTranslation("plans", { keyPrefix: "enum" });

  const { open, transaction, handleOpenModal, handleCloseModal } = useTransactionModal();

  return (
    <TableContainer>
      <Table aria-label="transactions table">
        <TableHead>
          <TableRow>
            <TableCell sx={styles.headCell}>{t("paymentReason")}</TableCell>
            <TableCell sx={styles.headCell}>{t("type")}</TableCell>
            <TableCell sx={styles.headCell}>{t("amount")}</TableCell>
            <TableCell sx={styles.headCell}>{t("cardNumber")}</TableCell>
            <TableCell sx={styles.headCell}>{t("date")}</TableCell>
            <TableCell sx={styles.headCell}>{t("status")}</TableCell>
            <TableCell sx={styles.headCell}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((transaction, index) => {
            const { id, paymentFor, type, amount, currency, createdAt, status, cardNumber } = transaction;
            return (
              <TableRow key={id || index} sx={styles.tableRow}>
                <TableCell sx={styles.bodyCell}>{tEnum(`transaction_payment_destination.${paymentFor}`)}</TableCell>
                <TableCell sx={styles.bodyCell}>{tEnum(`transaction_type.${type}`)}</TableCell>
                <TableCell sx={styles.bodyCell}>{amountFormatter(amount / 100, currency, i18n.language)}</TableCell>
                <TableCell sx={styles.bodyCell}>{formatCardNumber(cardNumber)}</TableCell>
                <TableCell sx={styles.bodyCell}>
                  <Box sx={styles.dateTimeContainer}>
                    <Typography sx={styles.dateText}>{format(new Date(createdAt), "dd/MM/yyyy")}</Typography>
                    <Typography sx={styles.timeText}>{formatLocalizedTimeAMPM(createdAt, i18n.language)}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={styles.bodyCell}>
                  <Box sx={styles.statusContainer(status === TransactionStatusEnum.Success)}>
                    <StatusIcon status={status} />
                    {tEnum(`transaction_status.${status}`)}
                  </Box>
                </TableCell>
                <TableCell sx={styles.bodyCell}>
                  <IconButton size="small" onClick={() => handleOpenModal(transaction)}>
                    <ViewIconButton />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TransactionModal transaction={transaction} open={open} closeModal={handleCloseModal} />
    </TableContainer>
  );
};

export default TransactionsTable;

const styles = {
  tableRow: {
    "&:last-child": {
      "& .MuiTableCell-root": {
        borderBottom: "none !important",
      },
    },
  },
  headCell: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "18px",
    borderBottom: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Gray-50, #F9FAFB)",
    whiteSpace: "nowrap",
    padding: "12px 16px",
  },
  bodyCell: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    borderBottom: "1px solid var(--Gray-200, #EAECF0)",
    padding: "24px 16px",
    whiteSpace: "nowrap",
  },
  dateTimeContainer: {
    display: "flex",
    flexDirection: "column",
  },
  dateText: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  timeText: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  statusContainer: (isSuccess: boolean) => ({
    width: "fit-content",
    background: isSuccess ? "#ECFDF3" : "#FEF3F2",
    color: isSuccess ? "#027A48" : "#B42318",
    display: "flex",
    padding: "2px 8px 2px 6px",
    alignItems: "center",
    gap: "4px",
    borderRadius: "16px",
    mixBlendMode: "multiply",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  }),
};
